import { ThemeType } from '../api/static/ThemeType';
import {
  GlassesGetter,
  prefixType as prefixGlassesType,
} from '../store/modules/configurator/glasses';

/**
 * Resolve configurator/glasses getters from a Vuex store and return compatible products.
 * Use this when you have access to the store and want to avoid repeating getter resolution.
 *
 * @param {Object} store - Vuex store instance (e.g. this.$store)
 * @param {Object} params
 * @param {number} params.productId - The product ID (theme) that was just added to cart
 * @param {number} params.sizeId - The current size ID that was ordered
 * @param {number} params.quantityId - The current quantity ID that was ordered
 * @param {ThemeType} params.themeType - The theme type (GENERIC or CUSTOM)
 * @returns {Array<{productVariant, product, configuration, printingArea, size, priceInfo}>} List of compatible size variants
 */
export function getCompatibleProductsFromStore(store, params) {
  const { productId, sizeId, quantityId, themeType } = params;

  const getSizesByProductIdGetter =
    store.getters[prefixGlassesType(GlassesGetter.GET_SIZE_BY_PRODUCT_ID)];
  const findProductVariantGetter =
    store.getters[prefixGlassesType(GlassesGetter.FIND_PRODUCT_VARIANT)];
  const findProductVariantsGetter =
    store.getters[prefixGlassesType(GlassesGetter.FIND_PRODUCT_VARIANTS)];
  const findProductByIdGetter =
    store.getters[prefixGlassesType(GlassesGetter.FIND_PRODUCT_BY_ID)];
  const findConfigurationByIdGetter =
    store.getters[prefixGlassesType(GlassesGetter.FIND_CONFIGURATION_BY_ID)];
  const findPrintingAreaByIdGetter =
    store.getters[prefixGlassesType(GlassesGetter.FIND_PRINTING_AREA_BY_ID)];

  return getCompatibleProducts({
    currentProductId: productId,
    themeType,
    currentSizeId: sizeId,
    currentQuantityId: quantityId,
    getSizesByProductIdGetter,
    findProductVariantGetter,
    findProductVariantsGetter,
    findProductByIdGetter,
    findConfigurationByIdGetter,
    findPrintingAreaByIdGetter,
  });
}

/**
 * Calculate compatible products for cross-selling based on the current product
 *
 * @param {Object} params
 * @param {number} params.currentProductId - The product ID (theme) that was just added to cart
 * @param {ThemeType} params.themeType - The theme type (GENERIC or CUSTOM)
 * @param {number} params.currentSizeId - The current size ID that was ordered
 * @param {number} params.currentQuantityId - The current quantity ID that was ordered
 * @param {function} params.getSizesByProductIdGetter - The GET_SIZE_BY_PRODUCT_ID getter from store
 * @param {function} params.findProductVariantGetter - The FIND_PRODUCT_VARIANT getter from store
 * @param {function} params.findProductVariantsGetter - The FIND_PRODUCT_VARIANTS getter from store
 * @param {function} params.findProductByIdGetter - The FIND_PRODUCT_BY_ID getter from store
 * @param {function} params.findConfigurationByIdGetter - The FIND_CONFIGURATION_BY_ID getter from store
 * @param {function} params.findPrintingAreaByIdGetter - The FIND_PRINTING_AREA_BY_ID getter from store
 * @returns {Array<{productVariant, product, configuration, printingArea, size}>} List of compatible size variants
 */
export function getCompatibleProducts({
  currentProductId,
  themeType,
  currentSizeId,
  currentQuantityId,
  getSizesByProductIdGetter,
  findProductVariantGetter,
  findProductVariantsGetter,
  findProductByIdGetter,
  findConfigurationByIdGetter,
  findPrintingAreaByIdGetter,
}) {
  // Only process for GENERIC theme types
  if (themeType !== ThemeType.GENERIC) {
    return [];
  }

  // Get all available sizes for this product/theme
  const availableSizes = getSizesByProductIdGetter(currentProductId);

  // Filter out the current size (already ordered)
  const compatibleSizes = availableSizes.filter(
    size => size.id !== currentSizeId
  );

  // Build the compatible products list with additional info
  const product = findProductByIdGetter(currentProductId);

  const compatibleProducts = compatibleSizes.map(size => {
    // Try to find a product variant for this size with the same quantity
    let productVariantWithSameQuantity = findProductVariantGetter(
      themeType,
      size.id,
      currentProductId,
      currentQuantityId
    );

    // Get any variant for configuration and printing area if needed
    let productVariantForConfig = productVariantWithSameQuantity;

    if (!productVariantForConfig) {
      // Get all variants for this size/product combination
      const allVariants = findProductVariantsGetter(
        themeType,
        size.id,
        currentProductId
      );

      // Take the first variant to get configuration and printing area
      // (they are the same for all quantities of the same size/product)
      if (allVariants && allVariants.length > 0) {
        productVariantForConfig = allVariants[0];
      }
    }

    // Extract price info
    // Unit prices are always available (don't depend on quantity)
    // Total prices only available if we have a variant with same quantity
    const priceInfo = {
      // Unit prices (quantity-independent)
      cupPrice: (productVariantWithSameQuantity || productVariantForConfig)
        ?.unitPrice,
      cupPriceWithoutVat: (
        productVariantWithSameQuantity || productVariantForConfig
      )?.unitPriceWithoutVAT,
      // Total prices (quantity-dependent) - only if same quantity variant exists
      totalPrice: productVariantWithSameQuantity?.price || null,
      totalPriceWithoutVat:
        productVariantWithSameQuantity?.priceWithoutVAT || null,
      // Indicate if we have complete price info
      hasCompletePrice: !!productVariantWithSameQuantity,
    };

    return {
      productVariant: productVariantWithSameQuantity || productVariantForConfig,
      product,
      size,
      // Configuration and printing area should now always be available
      configuration: productVariantForConfig
        ? findConfigurationByIdGetter(productVariantForConfig.configurationId)
        : null,
      printingArea: productVariantForConfig
        ? findPrintingAreaByIdGetter(productVariantForConfig.printingAreaId)
        : null,
      // Price info for the summary component
      priceInfo,
    };
  });

  return compatibleProducts;
}
